import { RouterLink } from "@/components/RouterLink";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { dragActiveHasCanvas } from "@/dnd/handlers/workspaceDetailDrop";
import { destinationFor, type DroppableData } from "@/dnd/ids";
import { cnTransferDropTargetSurface } from "@/dnd/transferDropTargetStyles";
import { cn } from "@/utils";
import type { MetadataIcon } from "@cohesive/api-client";
import { Icon as MetadataIconRenderer } from "@cohesive/ui-components";
import { useDroppable } from "@dnd-kit/core";
import React from "react";

/**
 * Breadcrumb dnd payload. Mirrors the data attached to FolderDropTarget /
 * the transfer dispatcher, so drops from ancestor crumbs route through the
 * same `workspace-folder` destination as folder cards.
 *
 * - `path` undefined targets the workspace root.
 * - `path` set (always trailing-slashed) targets a specific folder.
 */
export interface BreadcrumbDropTarget {
  workspaceId: string;
  path?: string;
}

export interface BreadcrumbEntry {
  label: string;
  to?: string;
  isLoading?: boolean;
  /**
   * Optional inline icon rendered immediately to the left of the
   * label. Used by the workspace name crumb to mirror the
   * workspace's chosen icon (so the breadcrumb visually matches the
   * sidebar / workspace card identity).
   */
  icon?: MetadataIcon | null;
  /**
   * If set, this crumb becomes a drop target. The page wires this up for
   * every ancestor crumb (workspace root + every parent folder) so users
   * can drag a deeply-nested item onto a breadcrumb to move it out.
   */
  dropTarget?: BreadcrumbDropTarget;
}

export function WorkspacePageHeader({
  breadcrumbs,
  action,
}: {
  breadcrumbs: BreadcrumbEntry[];
  action?: React.ReactNode;
}) {
  return (
    <div className="shrink-0 relative">
      <div className="mx-auto w-full max-w-[1600px] px-4 sm:px-6 md:px-8 lg:px-10 py-5 lg:py-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <Breadcrumb>
              <BreadcrumbList className="text-2xl font-normal font-poppins pt-2">
                {breadcrumbs.map((crumb, i) => {
                  const isLast = i === breadcrumbs.length - 1;
                  return (
                    <React.Fragment key={i}>
                      {i > 0 && <BreadcrumbSeparator />}
                      <BreadcrumbItem>
                        {crumb.isLoading ? (
                          <span className="inline-block h-5 w-32 rounded bg-muted animate-pulse" />
                        ) : (
                          <BreadcrumbCrumb crumb={crumb} isLast={isLast} />
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          {action}
        </div>
      </div>
    </div>
  );
}

function BreadcrumbCrumb({
  crumb,
  isLast,
}: {
  crumb: BreadcrumbEntry;
  isLast: boolean;
}) {
  /** Icon + label, kept as a single inline-flex unit so the icon
   *  always travels with the label when it truncates and so the
   *  whole pair shares the link's hover/click affordance. The icon
   *  itself sizes a bit smaller than the 24px breadcrumb text so
   *  it reads as a glyph paired *with* the label, not a sibling
   *  competing for attention.
   *
   *  Wrapping the label in `inline-flex items-center` shifts the
   *  rendered baseline up a hair compared to a bare text node
   *  (because flex centers on the icon's box, not the text's
   *  baseline). Nudging the whole pair down 2px restores the
   *  optical baseline of the iconless crumbs above/around it.
   */
  const labelContent = crumb.icon ? (
    <span className="inline-flex items-center gap-2 min-w-0 translate-y-px">
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center">
        <MetadataIconRenderer icon={crumb.icon} size={20} />
      </span>
      <span className="truncate">{crumb.label}</span>
    </span>
  ) : (
    crumb.label
  );

  const inner =
    isLast && !crumb.to ? (
      <BreadcrumbPage className="truncate font-light">
        {labelContent}
      </BreadcrumbPage>
    ) : crumb.to ? (
      <BreadcrumbLink asChild>
        <RouterLink className="truncate font-light" to={crumb.to}>
          {labelContent}
        </RouterLink>
      </BreadcrumbLink>
    ) : (
      <BreadcrumbPage className="truncate font-light">
        {labelContent}
      </BreadcrumbPage>
    );

  if (!crumb.dropTarget) {
    return inner;
  }

  return (
    <BreadcrumbDroppable target={crumb.dropTarget}>{inner}</BreadcrumbDroppable>
  );
}

function BreadcrumbDroppable({
  target,
  children,
}: {
  target: BreadcrumbDropTarget;
  children: React.ReactNode;
}) {
  // Drop id is keyed by the resolved target so concurrent crumbs don't
  // collide with FolderDropTarget ids elsewhere on the page. The data
  // shape matches FolderDropTarget / the workspace tab bar so the drag
  // dispatcher treats it identically.
  const dropId = target.path
    ? `breadcrumb-drop:${target.workspaceId}:${target.path}`
    : `breadcrumb-drop:${target.workspaceId}:root`;
  const parentPath = target.path ?? "";

  const droppableData: DroppableData = {
    surface: "workspace-folder",
    destination: destinationFor("workspace-folder", {
      workspaceId: target.workspaceId,
      parentPath,
    }),
  };

  const { setNodeRef, isOver, active } = useDroppable({
    id: dropId,
    data: droppableData,
  });

  const disallowed = isOver && parentPath !== "" && dragActiveHasCanvas(active);

  return (
    <span
      ref={setNodeRef}
      className={cnTransferDropTargetSurface({
        isOver,
        disallowed,
        className: cn("inline-flex items-center px-1.5 -mx-1.5 min-h-0"),
      })}
    >
      {children}
    </span>
  );
}
